import { Emulator, type DebugStopReason, type DebuggerRuleInfo } from "./emulator.js";
import type { Renderer } from "./renderer/types.js";
import { formatHexWord } from "./debugger-machine-state.js";

function formatStopRule(rule: DebuggerRuleInfo | null, pc: number): string {
  if (rule === null) {
    return `Stopped at breakpoint 0x${formatHexWord(pc)}.`;
  }
  if (rule.action === "trace_stop") {
    return `Trace+Stop matched: ${rule.matchText}.`;
  }
  if (isWatchpointExpression(rule.matchText)) {
    return `Stopped at watchpoint: ${rule.matchText}.`;
  }
  if (rule.anchorAddress !== null && isPlainAddressBreakpoint(rule.matchText)) {
    return `Stopped at breakpoint 0x${formatHexWord(rule.anchorAddress)}.`;
  }
  if (rule.anchorAddress !== null) {
    return `Stopped at breakpoint 0x${formatHexWord(rule.anchorAddress)}: ${rule.matchText}.`;
  }
  return `Stopped at breakpoint: ${rule.matchText}.`;
}

const RASTER_CYCLES_PER_LINE = 77;
const RASTER_LINES_TOTAL = 260;

type RunloopCallbacks = {
  onAfterRun?: () => void;
  onDebuggerStop?: (reason: DebugStopReason) => void;
  onMetrics?: (metrics: RunloopMetrics) => void;
  onRunningChange?: (running: boolean) => void;
  onSerial?: (data: Uint8Array) => void;
  onStatus?: (status: string) => void;
};

export type RunloopState = "running" | "paused_manual" | "paused_breakpoint" | "paused_step" | "halted";

export type RunloopMetrics = {
  cycleAccumulator: number;
  cycleCount: string;
  debuggerActive: boolean;
  frameBacklog: number;
  frameNo: number;
  halted: boolean;
  presentCount: number;
  presentedFrameNo: number;
  running: boolean;
  skippedFrameCount: number;
  state: RunloopState;
  stopReason: DebugStopReason;
};

type RunloopOptions = RunloopCallbacks & {
  clockHz?: number;
};

const DEFAULT_CLOCK_HZ = 1_000_000;
const MAX_FRAME_DELTA_MS = 50;
const DEBUG_SYNC_BATCH_CYCLES = 8_000;
const DEBUG_SYNC_CYCLE_LIMIT = 256_000;

function isPlainAddressBreakpoint(text: string): boolean {
  return /^(?:\$[0-9a-fA-F]{1,4}|0x[0-9a-fA-F]{1,4}|\d+)$/.test(text.trim());
}

function isWatchpointExpression(text: string): boolean {
  return /^on\s+(?:read|write|access)\s+mem\s*\[/i.test(text.trim());
}

function stopReasonToState(reason: DebugStopReason): RunloopState {
  switch (reason) {
    case "breakpoint":
      return "paused_breakpoint";
    case "step":
      return "paused_step";
    case "halted":
      return "halted";
    default:
      return "paused_manual";
  }
}

export class Runloop {
  private cycleAccumulator = 0;
  private readonly clockHz: number;
  private readonly callbacks: RunloopCallbacks;
  private debuggerExecutionEnabled = false;
  private lastPresentedFrame = -1;
  private lastTimestamp = 0;
  private pendingFrameBuffer: Uint8ClampedArray | null = null;
  private pendingFrameNo: number | null = null;
  private rafId = 0;
  private running = false;
  private skippedFrameCount = 0;
  private state: RunloopState = "paused_manual";
  private stopReason: DebugStopReason = "none";

  constructor(
    private readonly emulator: Emulator,
    private readonly renderer: Renderer,
    options: RunloopOptions = {}
  ) {
    this.clockHz = options.clockHz ?? DEFAULT_CLOCK_HZ;
    this.callbacks = options;
  }

  isRunning(): boolean {
    return this.running;
  }

  isDebuggerActive(): boolean {
    return this.debuggerExecutionEnabled || this.stopReason !== "none";
  }

  getState(): RunloopState {
    return this.state;
  }

  getStopReason(): DebugStopReason {
    return this.stopReason;
  }

  getSkippedFrameCount(): number {
    return this.skippedFrameCount;
  }

  getPresentedFrameNo(): number {
    return this.lastPresentedFrame >= 0 ? this.lastPresentedFrame : this.emulator.getFrameNo();
  }

  getFrameBacklog(): number {
    const presentedFrameNo = this.getPresentedFrameNo();
    const currentFrameNo = this.pendingFrameNo ?? this.emulator.getFrameNo();
    return Math.max(0, currentFrameNo - presentedFrameNo);
  }

  getCycleAccumulator(): number {
    return this.cycleAccumulator;
  }

  markFramePresented(): void {
    this.lastPresentedFrame = this.emulator.getFrameNo();
    this.pendingFrameNo = null;
  }

  activateDebugger(): void {
    this.syncDebuggerExecutionEnabled();
    this.emitMetrics();
  }

  start(): void {
    if (this.running) {
      return;
    }
    if (this.emulator.isHalted()) {
      this.finishDebuggerStop("halted");
      return;
    }

    if (this.debuggerExecutionEnabled) {
      this.emulator.continueDebugger();
    }

    this.running = true;
    this.state = "running";
    this.stopReason = "none";
    this.cycleAccumulator = 0;
    this.lastTimestamp = 0;
    this.callbacks.onRunningChange?.(true);
    this.callbacks.onStatus?.(this.debuggerExecutionEnabled ? "Debugger running..." : "Running browser host...");
    this.emitMetrics();
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (!this.running) {
      return;
    }

    this.running = false;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.state = "paused_manual";
    this.stopReason = "none";
    this.cycleAccumulator = 0;
    this.pendingFrameNo = null;
    this.syncDebuggerExecutionEnabled();
    this.callbacks.onRunningChange?.(false);
    this.callbacks.onStatus?.("Runloop paused.");
    this.emitMetrics();
  }

  pauseDebugger(): void {
    this.syncDebuggerExecutionEnabled();
    this.emulator.pauseDebugger();
    if (this.running) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    this.finishDebuggerStop("manual");
  }

  continueDebugger(): void {
    this.syncDebuggerExecutionEnabled();
    this.start();
  }

  stepDebugger(count = 1): void {
    this.syncDebuggerExecutionEnabled();
    if (this.running) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    this.emulator.stepDebugger(count);
    this.state = "running";
    this.stopReason = "none";
    this.emitMetrics();
    this.runDebuggerSynchronously();
  }

  private emitMetrics(): void {
    this.callbacks.onMetrics?.({
      cycleAccumulator: this.cycleAccumulator,
      cycleCount: this.emulator.getCycleCount().toString(),
      debuggerActive: this.isDebuggerActive(),
      frameBacklog: this.getFrameBacklog(),
      frameNo: this.emulator.getFrameNo(),
      halted: this.emulator.isHalted(),
      presentCount: this.renderer.getPresentCount(),
      presentedFrameNo: this.getPresentedFrameNo(),
      running: this.running,
      skippedFrameCount: this.skippedFrameCount,
      state: this.state,
      stopReason: this.stopReason
    });
  }

  private drainSerial(): void {
    const serial = this.emulator.drainSerialTx();
    if (serial.length > 0) {
      this.callbacks.onSerial?.(serial);
    }
  }

  private presentFrame(force = false): void {
    const frameNo = this.emulator.getFrameNo();
    if (force || frameNo !== this.lastPresentedFrame) {
      if (this.lastPresentedFrame >= 0 && frameNo > this.lastPresentedFrame + 1) {
        this.skippedFrameCount += frameNo - this.lastPresentedFrame - 1;
      }
      this.renderer.present(this.emulator.getFramebufferView());
      this.lastPresentedFrame = frameNo;
    }
  }

  private stageCompletedFrame(frameNo: number): void {
    const framebuffer = this.emulator.getFramebufferView();
    if (this.pendingFrameBuffer === null || this.pendingFrameBuffer.length !== framebuffer.length) {
      this.pendingFrameBuffer = new Uint8ClampedArray(framebuffer.length);
    }
    this.pendingFrameBuffer.set(framebuffer);
    this.pendingFrameNo = frameNo;
  }

  private presentPendingFrame(): void {
    if (this.pendingFrameNo === null || this.pendingFrameBuffer === null) {
      return;
    }
    if (this.lastPresentedFrame >= 0 && this.pendingFrameNo > this.lastPresentedFrame + 1) {
      this.skippedFrameCount += this.pendingFrameNo - this.lastPresentedFrame - 1;
    }
    this.renderer.present(this.pendingFrameBuffer);
    this.lastPresentedFrame = this.pendingFrameNo;
    this.pendingFrameNo = null;
  }

  private cyclesUntilNextFrameBoundary(): number {
    const voc = this.emulator.getVocState();
    return (
      (RASTER_LINES_TOTAL - voc.rasterLine - 1) * RASTER_CYCLES_PER_LINE +
      (RASTER_CYCLES_PER_LINE - voc.rasterCycle)
    );
  }

  private consumeCycleBudget(deltaMs: number): number {
    this.cycleAccumulator += (deltaMs * this.clockHz) / 1000;
    const cycleBudget = Math.floor(this.cycleAccumulator);
    this.cycleAccumulator -= cycleBudget;
    return cycleBudget;
  }

  private runCyclesToFrameBoundaries(cycleBudget: number): void {
    let remaining = cycleBudget;

    while (remaining > 0) {
      const frameBefore = this.emulator.getFrameNo();
      const chunk = Math.max(1, Math.min(remaining, this.cyclesUntilNextFrameBoundary()));
      const ran = this.emulator.runCycles(chunk);
      if (ran <= 0) {
        break;
      }
      remaining -= ran;

      const frameAfter = this.emulator.getFrameNo();
      if (frameAfter !== frameBefore) {
        this.stageCompletedFrame(frameAfter);
      }

      if (ran < chunk) {
        break;
      }
    }
  }

  private afterRun(presentMode: "live" | "pending" = "pending"): void {
    this.callbacks.onAfterRun?.();
    this.drainSerial();
    if (presentMode === "live") {
      this.presentFrame();
    } else {
      this.presentPendingFrame();
    }
    this.emitMetrics();
  }

  private debuggerStatus(reason: DebugStopReason): string {
    if (reason === "manual") {
      return "Debugger paused.";
    }

    const snapshot = this.emulator.getState();
    switch (reason) {
      case "breakpoint": {
        return formatStopRule(this.emulator.getLastDebuggerRule(), snapshot.pc);
      }
      case "step":
        return `Step complete at 0x${snapshot.pc.toString(16).toUpperCase().padStart(4, "0")}.`;
      case "halted":
        return `CPU halted after ${snapshot.cycles} cycles.`;
      default:
        return "Debugger idle.";
    }
  }

  private finishDebuggerStop(reason: DebugStopReason): void {
    const wasRunning = this.running;
    this.running = false;
    this.rafId = 0;
    this.state = stopReasonToState(reason);
    this.stopReason = reason;
    this.cycleAccumulator = 0;
    this.pendingFrameNo = null;
    this.syncDebuggerExecutionEnabled();
    this.presentFrame(true);
    if (wasRunning) {
      this.callbacks.onRunningChange?.(false);
    }
    this.callbacks.onStatus?.(this.debuggerStatus(reason));
    this.emitMetrics();
    this.callbacks.onDebuggerStop?.(reason);
  }

  private syncDebuggerExecutionEnabled(): void {
    const enabled = this.emulator.getDebuggerRuleCount() > 0;
    const changed = this.debuggerExecutionEnabled !== enabled;
    this.debuggerExecutionEnabled = enabled;
    if (changed && this.running && enabled) {
      this.emulator.continueDebugger();
    }
  }

  private runDebuggerSynchronously(): void {
    let totalRan = 0;

    while (totalRan < DEBUG_SYNC_CYCLE_LIMIT) {
      const budget = Math.min(DEBUG_SYNC_BATCH_CYCLES, DEBUG_SYNC_CYCLE_LIMIT - totalRan);
      const ran = this.emulator.runDebugCycles(budget);
      totalRan += ran;
      this.afterRun("live");

      const reason = this.emulator.getStopReason();
      if (reason !== "none") {
        this.finishDebuggerStop(reason);
        return;
      }
      if (ran === 0) {
        break;
      }
    }

    this.state = "paused_manual";
    this.stopReason = "manual";
    this.emitMetrics();
    throw new Error("debugger step did not reach a stop within the sync cycle budget");
  }

  private readonly tick = (timestamp: number): void => {
    if (!this.running) {
      return;
    }

    const deltaMs =
      this.lastTimestamp === 0
        ? 1000 / 60
        : Math.min(timestamp - this.lastTimestamp, MAX_FRAME_DELTA_MS);
    this.lastTimestamp = timestamp;

    const cycleBudget = this.consumeCycleBudget(deltaMs);
    if (cycleBudget <= 0) {
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.debuggerExecutionEnabled) {
      this.emulator.runDebugCycles(cycleBudget);
      this.afterRun("live");

      const reason = this.emulator.getStopReason();
      if (reason !== "none") {
        this.finishDebuggerStop(reason);
        return;
      }
    } else {
      this.runCyclesToFrameBoundaries(cycleBudget);
      this.afterRun("pending");
      if (this.emulator.isHalted()) {
        this.finishDebuggerStop("halted");
        return;
      }
    }

    this.rafId = requestAnimationFrame(this.tick);
  };
}
